import {waitUntil} from '@aboutyou/utils';
import {useCallback, useEffect, useRef} from 'react';

interface PageIdentity {
  path: string;
  id?: number;
}

/**
 * Resolves once the tracking client has recorded the content_view of the given
 * page, so events fired right after a navigation carry that page's type and id
 * instead of the previous page's. Overlays like the login modal fire a
 * content_view on the same path with their own id, which must not count.
 * Pages without an id in their context (e.g. search results) only match by path.
 */
export const waitUntilContentView = ({path, id}: PageIdentity) =>
  waitUntil(() => {
    const clientState = window.clientState;
    // Without a tracker state (noop client, e.g. in tests) there is no content_view to wait for.
    if (!clientState) {
      return true;
    }
    const currentPage = clientState.getCurrent().currentPage;

    return (
      currentPage?.type === 'detailed' &&
      currentPage.page.path === path &&
      (id === undefined || currentPage.page.id === id)
    );
  });

/**
 * Runs the callback once the page's content_view is tracked and drops the
 * waits still pending when the component unmounts.
 */
export const useWaitUntilContentView = () => {
  const pendingCancels = useRef(new Set<() => void>());

  useEffect(
    () => () => {
      pendingCancels.current.forEach(cancel => cancel());
      pendingCancels.current.clear();
    },
    [],
  );

  return useCallback((page: PageIdentity, onContentView: () => void) => {
    const {waiter, cancel} = waitUntilContentView(page);
    pendingCancels.current.add(cancel);

    waiter.then(() => {
      pendingCancels.current.delete(cancel);
      onContentView();
    });
  }, []);
};
